import { Util } from 'src/shared/util/util';
import { Component, OnInit } from '@angular/core';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { BookJob } from 'src/providers/book-job/book-job';
import { Schedule } from 'src/providers/schedule/schedule';
import { Booking } from 'src/providers/booking/booking';

@Component({
    selector: 'confirm-request-page',
    styleUrls: ['./confirm-request.scss'],
    templateUrl: './confirm-request.html',
    standalone: false
})
export class ConfirmRequestPage implements OnInit {
  address: any;
  displayPrice: any;
  flowType: string;
  jobId: any;
  loaded: boolean;
  bookingType;
  viewData;
  errorMessage;
  params: any;
  proName: string;
  dialogParams: any;
  isRightSideContent = true;
  submitted = false;
  alreadyAccepted = false;

  
  boundGoBack = this.goBack.bind(this);
  boundConfirmBooking = this.confirmBooking.bind(this);

constructor(
    private navCtrl: CustomNavController,
    private storage: TidyStorage,
    private rightSidePanelService: RightSidePanelService,
    private bookJob: BookJob,
    private util: Util,
    private schedule: Schedule,
    private booking: Booking
  ) {}

  async ngOnInit() {
    try {
      this.isRightSideContent =
        (await this.storage.retrieve('dialog-right-side-open')) || false;
      this.dialogParams = await this.storage.retrieve('dialog-params');
      await this.loadDataAndParams();
      await this.checkIfAlreadyAccepted();
      this.loaded = true;
    } catch (_error) {
      // Intentionally empty - errors handled elsewhere
    }
  }

  async loadDataAndParams() {
    this.viewData = await this.getParam('viewData');
    this.params = this.dialogParams || (await this.navCtrl.getParams());
    this.address = this.params?.address || this.params?.bookingDetail?.address || this.params?.cardDetail?.template_data?.booking?.address;
  }

  // Detect re-entry after a prior successful acceptBid (user navigated away and came back, or
  // signed out and back in). The BE check covers cleared storage; the storage check is a fast
  // path for the common in-session case so we don't always hit the network on init.
  private async checkIfAlreadyAccepted() {
    if (await this.isInStoredAcceptedData() || await this.hasAcceptedBidOnServer()) {
      this.alreadyAccepted = true;
    }
  }

  private async isInStoredAcceptedData(): Promise<boolean> {
    const jobId = this.viewData?.job_id;
    const homekeeperId = this.viewData?.homekeeper_id;
    const bookingId = this.viewData?.booking_id;
    if (!jobId || !homekeeperId || !bookingId) {
      return false;
    }
    const storedHomekeeperData = await this.storage.retrieve('stored-homekeeper-data') || {};
    const existing = storedHomekeeperData[jobId];
    return existing?.homekeeper_id === homekeeperId && existing?.booking_id === bookingId;
  }

  private async hasAcceptedBidOnServer(): Promise<boolean> {
    const bookingId = this.viewData?.booking_id;
    if (!bookingId) {
      return false;
    }
    try {
      const bids = await this.booking.getHomekeeperBids(bookingId);
      if (!Array.isArray(bids)) {
        return false;
      }
      return bids.some((bid: any) =>
        bid?.rates?.some((rate: any) =>
          rate?.times?.some((t: any) =>
            t?.status === 'accepted_by_customer' || t?.status === 'accepted_by_both'
          )
        )
      );
    } catch {
      return false;
    }
  }

  private navigateAfterAccept() {
    if (this.isRightSideContent) {
      this.schedule.setReloadSchedulePage();
      this.rightSidePanelService.goBackInLayers(2);
    } else {
      this.navCtrl.navigateRoot('schedule');
    }
  }

  goBack() {
    if (this.isRightSideContent) {
      this.rightSidePanelService.goBack();
      return;
    }
    const customBack = this.navCtrl.getParam('customBack', false);
    const redirectPage = customBack ? customBack : 'book-job';
    this.navCtrl.navigateBack(redirectPage);
  }

  async confirmBooking() {
    if (this.submitted || this.alreadyAccepted) {
      return;
    }
    this.submitted = true;
    const loading = await this.util.showLoading();
    try {
      // Verify against BE before firing acceptBid — defends against cleared storage / signed-out
      // re-entry where the in-memory and storage checks would both miss the prior acceptance.
      if (await this.hasAcceptedBidOnServer()) {
        this.alreadyAccepted = true;
        loading.dismiss();
        this.util.showSuccess('Quote already accepted — we\'ll confirm with your pro and follow up soon.');
        this.navigateAfterAccept();
        return;
      }
      const params = {
        booking_id: this.viewData.booking_id,
        homekeeper_id: this.viewData.homekeeper_id,
        amount: this.viewData.price,
        date: this.viewData.date,
        time: this.viewData.time,
        instant_booking: this.viewData?.instant_booking === null ? true : this.viewData?.instant_booking,
      };
      await this.bookJob.acceptBid(params);

      // Save homekeeper data to storage for display on job page
      if (this.viewData.homekeeper_id && this.viewData.homekeeper_name) {
        try {
          const jobId = this.viewData.job_id;
          if (jobId) {
            const storedHomekeeperData = await this.storage.retrieve('stored-homekeeper-data') || {};
            storedHomekeeperData[jobId] = {
              job_id: jobId,
              homekeeper_id: this.viewData.homekeeper_id,
              homekeeper_name: this.viewData.homekeeper_name,
              booking_id: this.viewData.booking_id
            };
            await this.storage.save('stored-homekeeper-data', storedHomekeeperData);
          }
        } catch (storageError) {
          console.error('Error saving homekeeper data to storage:', storageError);
          // Don't block the flow if storage fails
        }
      }

      loading.dismiss();
      this.alreadyAccepted = true;
      this.util.showSuccess('Quote accepted — we\'ll confirm with your pro and follow up soon.');
      this.navigateAfterAccept();
    } catch (error) {
      const errorMessage = error?.error?.message || error?.err?.error?.message || 'Something went wrong';
      this.util.showError(errorMessage);
      loading.dismiss();
      this.submitted = false;
    }
  }

  async getParam(param) {
    const navParam = this.navCtrl.getParam(param, false);
    if (navParam) {
      return navParam;
    } else {
      const storedParam =
        this.dialogParams ||
        (await this.storage.retrieve('confirmBookingPaymentParams'));
      return storedParam?.[param];
    }
  }
}
